import React, { useEffect, useState } from 'react';
import { Bot, Loader2, Coins, Plus, Ban, Check } from 'lucide-react';
import { AdminAgentAccount, adminListAgentAccounts, adminGrantAgentCredits } from '../services/agentKeyService';

const AdminAgentsView: React.FC = () => {
  const [agents, setAgents] = useState<AdminAgentAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [granted, setGranted] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<Record<string, number>>({});

  const load = async () => {
    setLoading(true); setErr(null);
    try { setAgents(await adminListAgentAccounts()); }
    catch (e: any) { setErr(e.message || 'Could not load agents (CTO only).'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grant = async (a: AdminAgentAccount) => {
    const amt = amounts[a.id];
    if (!amt) return;
    setBusy(a.id); setErr(null);
    try {
      const bal = await adminGrantAgentCredits(a.id, amt);
      setAgents((prev) => prev.map((x) => x.id === a.id ? { ...x, credit_balance: bal } : x));
      setAmounts((m) => ({ ...m, [a.id]: 0 }));
      setGranted(a.id); setTimeout(() => setGranted(null), 1500);
    } catch (e: any) { setErr(e.message || 'Grant failed.'); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5 animate-in fade-in duration-300">
      <div className="border-b border-slate-700 pb-4">
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><Bot className="text-indigo-400" size={20} /> Agent API keys</h1>
        <p className="text-slate-400 text-sm mt-1">All agent keys across users. Grant credits (100 credits = $1.00).</p>
      </div>

      {err && <div className="bg-red-900/20 border border-red-500/30 p-3 rounded-lg text-red-200 text-sm">{err}</div>}

      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-indigo-500" /></div> : agents.length === 0 ? (
        <p className="text-slate-500 text-center py-10">No agent keys yet.</p>
      ) : (
        <div className="space-y-2">
          {agents.map((a) => (
            <div key={a.id} className={`bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-wrap items-center gap-3 ${!a.active ? 'opacity-60' : ''}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium truncate">{a.name}</span>
                  {!a.active && <span className="text-[10px] bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded flex items-center gap-1"><Ban size={10} /> revoked</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{a.owner_email || 'unknown owner'} · <code>{a.key_prefix || 'tinaney_'}…</code></div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1" title="Balance"><Coins size={13} className="text-yellow-400" /> {a.credit_balance}</span>
                <span title="Requests">{a.calls} calls</span>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" min={0} value={amounts[a.id] ?? ''} onChange={(e) => setAmounts((m) => ({ ...m, [a.id]: parseInt(e.target.value) || 0 }))}
                  placeholder="credits" className="w-24 bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-indigo-500" />
                <button onClick={() => grant(a)} disabled={busy === a.id || !amounts[a.id]}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
                  {busy === a.id ? <Loader2 size={14} className="animate-spin" /> : granted === a.id ? <Check size={14} /> : <Plus size={14} />} Grant
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAgentsView;
